import type { TournamentWSClient } from '../sdk/client'
import type { OperationStore } from './operation-store'
import type { PlayerMetrics } from '../sdk'
import { notifyConnectionLost, notifyConnectionRestored } from '../notifications'
import { filterLocalPlayerOperations } from './operation-owner'
import { chartDebugLog } from '../debug/browser-logging'

export interface ReconciliationManagerConfig {
  /**
   * Called when metrics are successfully fetched during resync.
   * Allows external consumers to update UI/state with latest metrics.
   */
  onMetrics?: (metrics: PlayerMetrics[]) => void
  /**
   * Local player userId for filtering operations during resync.
   * When provided, only operations belonging to this player are hydrated.
   */
  localUserId?: string
}

/**
 * Manages state reconciliation via resync operations.
 *
 * Responsibilities:
 * 1. Listen to websocket connection state changes and trigger resync on reconnect
 * 2. Provide explicit `resync()` method for error-recovery call sites
 * 3. Guard against concurrent resyncs with simple boolean flag
 * 4. Preserve marker dedup and cap policy during hydration
 *
 * Resync flow:
 * - Call `listAllOperations()` → dispatch `HYDRATE` to store
 * - Call `metrics()` → route to `onMetrics` callback
 * - On failure: log error and bail (no automatic retry; next trigger will retry)
 *
 * Reconnect behavior:
 * - Skip initial connect (TournamentApp handles initial hydration)
 * - Trigger resync only on subsequent reconnects (tracked via `initialConnectDone` flag)
 */
export class ReconciliationManager {
  private syncing = false
  private initialConnectDone = false
  private lastSyncTimestamp = 0
  private unsubscribeFns: (() => void)[] = []

  constructor(
    private wsClient: TournamentWSClient,
    private store: OperationStore,
    private config: ReconciliationManagerConfig = {},
  ) {}

  /**
   * Manually trigger a resync operation.
   * Safe to call from error recovery handlers.
   * Guarded by `syncing` flag to prevent concurrent resyncs.
   */
  async resync(): Promise<void> {
    if (this.syncing) {
      chartDebugLog('reconciliation', 'resync already in progress, skipping', {})
      return
    }

    this.syncing = true

    try {
      chartDebugLog('reconciliation', 'starting resync', {})
      const startTime = Date.now()

      // Fetch operation snapshot and hydrate store
      let allOps = await this.wsClient.listAllOperations()

      // Filter to local player only if localUserId is configured
      if (this.config.localUserId) {
        allOps = filterLocalPlayerOperations(allOps, this.config.localUserId)
      }

      this.store.dispatch({ type: 'HYDRATE', operations: allOps })
      chartDebugLog('reconciliation', 'hydrated operations', { count: allOps.length })

      // Fetch metrics (if callback provided)
      if (this.config.onMetrics) {
        const metrics = await this.wsClient.metrics()
        this.config.onMetrics(metrics)
        chartDebugLog('reconciliation', 'updated metrics', { playerCount: metrics.length })
      }

      const elapsed = Date.now() - startTime
      this.lastSyncTimestamp = Date.now()
      chartDebugLog('reconciliation', 'resync completed', { elapsedMs: elapsed })
    } catch (error) {
      console.error('[ReconciliationManager] Resync failed:', error)
      // Do not retry automatically — let next trigger (reconnect/mutation error) retry
    } finally {
      this.syncing = false
    }
  }

  /**
   * Attach to websocket connection state changes.
   * Automatically triggers resync on reconnect (not on initial connect).
   *
   * Returns unsubscribe function for cleanup.
   */
  attach(): () => void {
    const unsubscribe = this.wsClient.onStateChange((state) => {
      if (state === 'connected') {
        if (!this.initialConnectDone) {
          // Skip initial connect — TournamentApp handles initial hydration
          this.initialConnectDone = true
          chartDebugLog('reconciliation', 'initial connect detected, skipping resync', {})
          return
        }

        // This is a reconnect — trigger resync and notify user
        chartDebugLog('reconciliation', 'reconnect detected, triggering resync', {})
        notifyConnectionRestored()
        this.resync().catch((error) => {
          console.error('[ReconciliationManager] Resync on reconnect failed:', error)
        })
      } else if (state === 'reconnecting' && this.initialConnectDone) {
        // Only notify about reconnection attempts after initial connect
        chartDebugLog('reconciliation', 'reconnection attempt detected', {})
        notifyConnectionLost()
      }
    })

    this.unsubscribeFns.push(unsubscribe)
    return unsubscribe
  }

  /**
   * Subscribe to parse errors from the websocket client
   * and trigger resync on shape/invariant failures.
   * (This method subscribes to a custom event emitted by TournamentWSClient)
   */
  attachToParseErrors(wsClientEventEmitter: { on: (event: string, handler: () => void) => () => void }): () => void {
    const unsubscribe = wsClientEventEmitter.on('PARSE_ERROR', () => {
      chartDebugLog('reconciliation', 'parse error detected, triggering resync', {})
      this.resync().catch((error) => {
        console.error('[ReconciliationManager] Resync on parse error failed:', error)
      })
    })

    this.unsubscribeFns.push(unsubscribe)
    return unsubscribe
  }

  /**
   * Clean up all subscriptions.
   * Call this in component cleanup or app teardown.
   */
  destroy(): void {
    this.unsubscribeFns.forEach((fn) => fn())
    this.unsubscribeFns = []
    chartDebugLog('reconciliation', 'destroyed', {})
  }

  /**
   * Get current syncing state (for testing/debugging).
   */
  isSyncing(): boolean {
    return this.syncing
  }

  /**
   * Get timestamp of last successful sync (for debugging).
   */
  getLastSyncTimestamp(): number {
    return this.lastSyncTimestamp
  }
}
